import { StackProps, XStack } from '@mythical/ui'
import Section from 'app/components/structure/Section'
import { ContentCardProps } from '../ContentCard'
import { useCarouselController } from 'app/hooks/use-carousel-controller'
import { ContentCarousel } from 'app/components/content/ContentCarousel/ContentCarousel'
import { ContentCardBookmarkable } from 'app/components/content/ContentCard/ContentCardBookmarkable'
import { IconChevronRight } from 'app/components/primitive/Icon'
import { Button } from 'app/components/primitive/Button'
import { IconChevronLeft } from 'app/components/primitive/Icon'
import { useCallback, useMemo } from 'react'
import {
  WatchLaterProvider,
  useWatchLaterContext,
} from 'app/providers/WatchLaterProvider'

type ContentCarouselProps = {
  items: ContentCardProps[]
  title: string
  onNavigate?: () => void
  isVideo?: boolean
} & StackProps

const normaliseDate = (date: string) => {
  if (!date) return new Date()
  const [day, month, year] = date.split('/')
  return new Date(`${year}-${month}-${day}`)
}

const RelatedContentInner = ({
  title,
  items,
  onNavigate,
  isVideo = false,
  ...stackProps
}: ContentCarouselProps) => {
  const { carouselRef, ...controller } = useCarouselController()
  const { isInWatchLater, toggleWatchLater } = useWatchLaterContext()

  const sortedItems = useMemo(() => {
    if (!isVideo || !items) return items
    return [...items].sort((a, b) => {
      const datePublishedA: Date = normaliseDate(a?.datePublished)
      const datePublishedB: Date = normaliseDate(b?.datePublished)
      return datePublishedB?.valueOf() - datePublishedA?.valueOf()
    })
  }, [items, isVideo])

  const renderItem = useCallback(
    (item: ContentCardProps) => (
      <ContentCardBookmarkable
        {...item}
        onNavigate={onNavigate}
        isInWatchLater={isInWatchLater(item.id)}
        onToggleWatchLater={toggleWatchLater}
      />
    ),
    [onNavigate, isInWatchLater, toggleWatchLater],
  )

  const keyExtractor = useCallback(
    (item: ContentCardProps) => `${item.id}-${item.href}`,
    [],
  )

  return (
    <Section
      title="More"
      titleHighlight={title}
      customCta={
        <XStack gap="$2" $sm={{ dsp: 'none' }}>
          <Button
            theme="tertiary"
            onPress={controller.scrollToPrevious}
            disabled={!controller.canScrollPrevious}
            aria-label="Previous"
            icon={<IconChevronLeft size={16} color="$fgPrimary" />}
            size="$3.5"
            circular
          />
          <Button
            theme="tertiary"
            onPress={controller.scrollToNext}
            disabled={!controller.canScrollNext}
            aria-label="Next"
            circular
            size="$3.5"
            icon={<IconChevronRight size={16} color="$fgPrimary" />}
          />
        </XStack>
      }
      {...stackProps}
    >
      <ContentCarousel
        items={sortedItems}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        controllerRef={carouselRef}
      />
    </Section>
  )
}

const RelatedContent = ({
  items,
  ...props
}: ContentCarouselProps) => {
  const contentIds = useMemo(() => items?.map((item) => item.id) || [], [items])

  if (items?.length === 0) {
    return null
  }

  return (
    <WatchLaterProvider contentIds={contentIds}>
      <RelatedContentInner items={items} {...props} />
    </WatchLaterProvider>
  )
}

export default RelatedContent
